import { theme, type ThemeConfig } from 'antd';

/**
 * Ant Design 5 theme configuration for Trade Gladiator Admin Portal
 * Supports both light and dark modes
 */

// Brand colors
const brandPrimary = '#1890ff';
const brandSuccess = '#52c41a';
const brandError = '#ff4d4f';
const brandWarning = '#faad14';

// Financial colors
const profitGreen = '#52c41a';
const lossRed = '#ff4d4f';

export const lightTheme: ThemeConfig = {
  token: {
    // Brand colors
    colorPrimary: brandPrimary,
    colorSuccess: brandSuccess,
    colorError: brandError,
    colorWarning: brandWarning,

    // Typography
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontSize: 14,

    // Border radius
    borderRadius: 6,
    borderRadiusLG: 8,
    borderRadiusSM: 4,

    // Spacing
    padding: 16,
    paddingLG: 24,
    paddingSM: 12,
    paddingXS: 8,

    // Motion
    motionDurationFast: '0.1s',
    motionDurationMid: '0.2s',
    motionDurationSlow: '0.3s',
  },
  components: {
    Layout: {
      headerBg: '#fff',
      siderBg: '#001529',
      bodyBg: '#f5f5f5',
    },
    Menu: {
      // The admin sider menu always renders in dark theme (AppLayout forces
      // menuProps={{ theme: 'dark' }}) because the sider is always dark navy.
      // These dark-theme tokens give the sider its readable light labels —
      // keep them explicit so the contrast can't silently regress.
      darkItemBg: '#001529',
      darkItemColor: 'rgba(255, 255, 255, 0.65)',
      darkItemHoverColor: '#fff',
      darkItemSelectedBg: brandPrimary,
      darkItemSelectedColor: '#fff',
    },
    Card: {
      paddingLG: 24,
    },
    Table: {
      headerBg: '#fafafa',
    },
  },
};

export const darkTheme: ThemeConfig = {
  // Run antd's dark algorithm so EVERY token (component backgrounds, borders,
  // hovers, inputs, dropdowns, disabled states, etc.) is derived dark — not
  // just the handful overridden below. Without this, dark mode ran the default
  // (light) algorithm and only the explicitly-overridden surfaces went dark,
  // leaving the rest light (the dark-mode token desync). The token overrides
  // below remain as intentional pins layered on top of the algorithm output.
  algorithm: theme.darkAlgorithm,
  token: {
    // Brand colors
    colorPrimary: brandPrimary,
    colorSuccess: brandSuccess,
    colorError: brandError,
    colorWarning: brandWarning,

    // Dark mode specific
    colorBgContainer: '#141414',
    colorBgElevated: '#1f1f1f',
    colorBgLayout: '#000000',
    colorBgSpotlight: '#1f1f1f',
    colorBorder: '#303030',
    colorBorderSecondary: '#1f1f1f',
    colorText: 'rgba(255, 255, 255, 0.85)',
    colorTextSecondary: 'rgba(255, 255, 255, 0.65)',
    colorTextTertiary: 'rgba(255, 255, 255, 0.45)',
    colorTextQuaternary: 'rgba(255, 255, 255, 0.25)',

    // Typography
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontSize: 14,

    // Border radius
    borderRadius: 6,
    borderRadiusLG: 8,
    borderRadiusSM: 4,
  },
  components: {
    Layout: {
      headerBg: '#141414',
      siderBg: '#001529',
      bodyBg: '#000000',
    },
    Card: {
      colorBgContainer: '#141414',
    },
    Table: {
      headerBg: '#1f1f1f',
      colorBgContainer: '#141414',
    },
  },
};

// Financial color tokens for profit/loss display
export const financialColors = {
  profit: profitGreen,
  loss: lossRed,
  neutral: '#8c8c8c',
};

// Get theme based on mode
export const getTheme = (mode: 'light' | 'dark'): ThemeConfig => {
  return mode === 'dark' ? darkTheme : lightTheme;
};
